import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import firebase from "firebase/compat/app";
import "firebase/compat/storage";
import Compressor from "compressorjs";
import { DateTime } from "luxon";
import Common, { AUTHORIZATION_LEVEL } from "../../Common/Common";
import DataBase from "../../dataBase/liquidGuides";
import AlertModal from "./AlertModal";
import "./AttachmentsList.css";

const MAX_PDF_MB = 25;

// Generic "attach a file, see a list, delete one" panel — reusable across
// features via the `context` prop (a plain string tag, e.g.
// "traveler_document:<travelerId>:<documentId>"), not a full modal/picker
// like ImageDialog.js. PDFs upload as-is; images reuse the same dual-size
// Compressor pipeline ImageDialog.js already uses (width 800 -> url, width
// 150 -> url_m) so images stored here are consistent with images stored
// elsewhere in the app.
const AttachmentsList = ({ context, user_info, Authorization }) => {
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [alertShow, setAlertShow] = useState(false);
  const [alertTitle, setAlertTitle] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [alertButtons, setAlertButtons] = useState([]);

  const isGuest =
    !user_info?.uid || user_info.uid === "guest" || Authorization === AUTHORIZATION_LEVEL.GUEST;

  const refresh = useCallback(() => {
    if (isGuest || !context) {
      setAttachments([]);
      return;
    }
    DataBase.getAttachments(context, user_info.uid, (res) => {
      if (res.pass) setAttachments(res.res);
    });
  }, [context, user_info?.uid, isGuest]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const folderPath = () => Common.getFBPath(null, user_info, "fb_documents");

  const uploadFailed = (error) => {
    console.log("Attachment upload failed:", error);
    setUploading(false);
    setAlertTitle("Upload Failed");
    setAlertMessage(
      `Couldn't upload that file: ${error?.message || error}. If this keeps happening, the storage permissions for attachments may need to be set up.`
    );
    setAlertButtons([{ Title: "OK", Method: null }]);
    setAlertShow(true);
  };

  const uploadBlob = (blob, fileName) => {
    const ref = firebase.storage().ref().child(`${folderPath()}${Date.now()}_${fileName}`);
    return ref.put(blob).then(() => ref.getDownloadURL());
  };

  const finishUpload = (meta) => {
    DataBase.addAttachment(meta, (res) => {
      setUploading(false);
      if (res.pass) refresh();
      else uploadFailed(res.res);
    });
  };

  const uploadPdf = (file) => {
    uploadBlob(file, file.name)
      .then((url) => {
        finishUpload({
          user_id: user_info.uid,
          context,
          name: file.name,
          type: "pdf",
          url,
          date: DateTime.utc().toISO(),
        });
      })
      .catch(uploadFailed);
  };

  const uploadImage = (file) => {
    new Compressor(file, {
      quality: 0.6,
      resize: "cover",
      width: 800,
      success: (medium) => {
        new Compressor(file, {
          quality: 0.6,
          resize: "cover",
          width: 150,
          success: (thumb) => {
            Promise.all([uploadBlob(medium, file.name), uploadBlob(thumb, `${file.name}_m`)])
              .then(([url, url_m]) => {
                finishUpload({
                  user_id: user_info.uid,
                  context,
                  name: file.name,
                  type: "image",
                  url,
                  url_m,
                  date: DateTime.utc().toISO(),
                });
              })
              .catch(uploadFailed);
          },
          error: uploadFailed,
        });
      },
      error: uploadFailed,
    });
  };

  const onFileChosen = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    // Matches the 25 MB fb_documents limit in the Storage rules
    // (docs/StorageRulesLiquidguides.txt). Images are resized before upload.
    if (file.type === "application/pdf" && file.size >= MAX_PDF_MB * 1024 * 1024) {
      setAlertTitle("File Too Large");
      setAlertMessage(
        `That PDF is ${(file.size / (1024 * 1024)).toFixed(1)} MB. The limit is ${MAX_PDF_MB} MB — try a smaller or compressed copy.`
      );
      setAlertButtons([{ Title: "OK", Method: null }]);
      setAlertShow(true);
      return;
    }
    setUploading(true);
    if (file.type === "application/pdf") uploadPdf(file);
    else if (file.type.startsWith("image/")) uploadImage(file);
    else {
      setUploading(false);
      setAlertTitle("Message");
      setAlertMessage("Only PDF and image files are supported.");
      setAlertButtons([{ Title: "OK", Method: null }]);
      setAlertShow(true);
    }
  };

  const deleteAttachment = (attachment) => {
    setAlertTitle("Confirmation");
    setAlertMessage("Confirm Delete Attachment?");
    setAlertButtons([
      {
        Title: "OK",
        Method: () => {
          const urls = [attachment.data.url, attachment.data.url_m].filter(Boolean);
          DataBase.deleteFromStorage(urls, () => {
            DataBase.deleteAttachment(attachment.id, () => refresh());
          });
        },
      },
      { Title: "CANCEL", Method: null },
    ]);
    setAlertShow(true);
  };

  if (isGuest) {
    return <div className="al-guest-message">Sign in to attach files.</div>;
  }

  return (
    <div className="al-shell">
      <div className="al-header-row">
        <span className="al-label">Attachments</span>
        <label className="al-add-btn" title="Add file">
          <input type="file" accept="application/pdf,image/*" onChange={onFileChosen} disabled={uploading} />
          <FontAwesomeIcon icon={solid("plus")} />
        </label>
      </div>

      {attachments.length === 0 && !uploading && <div className="al-empty">No files attached</div>}

      <div className="al-list">
        {attachments.map((att) => (
          <div className="al-item" key={att.id}>
            {att.data.type === "image" ? (
              <img className="al-thumb" src={att.data.url_m} alt="" />
            ) : (
              <span className="al-file-icon">
                <FontAwesomeIcon icon={solid("file-pdf")} />
              </span>
            )}
            <a className="al-item-name" href={att.data.url} target="_blank" rel="noopener noreferrer">
              {att.data.name}
            </a>
            <button className="al-delete-btn" title="Delete" onClick={() => deleteAttachment(att)}>
              <FontAwesomeIcon icon={solid("trash")} />
            </button>
          </div>
        ))}
        {uploading && (
          <div className="al-item al-uploading">
            <span className="al-file-icon">
              <FontAwesomeIcon icon={solid("spinner")} spin />
            </span>
            <span className="al-item-name">Uploading…</span>
          </div>
        )}
      </div>

      <AlertModal
        show={alertShow}
        onHide={() => setAlertShow(false)}
        AlertModalTitle={alertTitle}
        AlertModalMessage={alertMessage}
        AlertModalButtons={alertButtons}
      />
    </div>
  );
};

export default AttachmentsList;
